import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, Facebook } from "lucide-react";
import { useLang } from "@/i18n";
import { EASE, FACEBOOK, WHATSAPP } from "@/lib/site";

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay }}>
    {children}
  </motion.div>
);

const Painting = ({ y, scale }) => (
  <motion.div style={{ y, scale }} className="relative h-full flex justify-center lg:justify-start">
    <img src="/rishi-lineage.jpg" alt="" aria-hidden="true" className="glow-mask absolute inset-0 w-full h-full object-cover blur-3xl opacity-30 saturate-150 scale-110" />
    <motion.img
      data-testid="hero-image"
      src="/rishi-lineage.jpg"
      alt="Painting of the lineage of Rishis along a sacred river"
      className="hero-mask relative h-full w-auto max-w-none object-contain"
      initial={{ opacity: 0, scale: 1.08, filter: "blur(12px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      transition={{ duration: 2.2, ease: EASE }}
    />
  </motion.div>
);

const Ctas = ({ t }) => (
  <div className="flex flex-wrap gap-3 lg:justify-end">
    <Link data-testid="hero-book-btn" to="/book" className="pill-solid px-7 py-3.5 text-sm group">
      {t.hero.book} <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:rotate-45" />
    </Link>
    <a data-testid="hero-whatsapp-btn" href={WHATSAPP} target="_blank" rel="noreferrer" className="pill-ghost px-6 py-3.5 text-sm">
      <MessageCircle className="w-4 h-4 mr-2" /> {t.hero.whatsapp}
    </a>
    <a data-testid="hero-facebook-btn" href={FACEBOOK} target="_blank" rel="noreferrer" className="pill-ghost px-6 py-3.5 text-sm">
      <Facebook className="w-4 h-4 mr-2" /> {t.hero.facebook}
    </a>
  </div>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[780px] h-[780px] pointer-events-none" aria-hidden="true"><div className="mandala w-full h-full" /></div>
      <div className="relative mx-auto max-w-[1440px] lg:grid lg:grid-cols-12 min-h-[100svh]">
        <div className="lg:col-span-5 h-[62svh] lg:h-[100svh] pt-[72px] lg:pt-0">
          <Painting y={y} scale={scale} />
        </div>
        <motion.div key={lang} style={{ y: textY }} className="lg:col-span-7 relative z-10 -mt-24 lg:mt-0 px-5 sm:px-8 lg:pr-12 pb-20 lg:pt-28 lg:pb-16 flex flex-col justify-center lg:items-end lg:text-right">
          <Fade delay={0.1}><img data-testid="hero-logo" src="/logo.png" alt="Vedic Yagya Astrology" className="w-28 sm:w-32 lg:w-36 mb-6" /></Fade>
          <Fade delay={0.2}><p data-testid="hero-eyebrow" className="eyebrow mb-7">{t.hero.eyebrow}</p></Fade>
          <h1 data-testid="hero-title" className="display text-[3.4rem] sm:text-7xl lg:text-[6rem] xl:text-[7.25rem] leading-[0.92] tracking-[-0.02em]">
            <Line delay={0.35}>{t.hero.title1}</Line>
            <Line delay={0.5} className="italic text-[color:var(--gold)]">{t.hero.title2}</Line>
          </h1>
          <Fade delay={0.9} className="mt-7 flex items-center gap-4 lg:flex-row-reverse">
            <span className="h-px w-14 bg-[color:var(--saffron)]" />
            <p data-testid="hero-subtitle" className="display italic text-2xl sm:text-3xl text-[color:var(--saffron-soft)]">{t.hero.subtitle}</p>
          </Fade>
          <Fade delay={1.05}><p className="mt-5 text-sm uppercase tracking-[0.28em] text-[color:var(--muted)]">{t.hero.by}</p></Fade>
          <Fade delay={1.15}><p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-[color:var(--muted)] lg:ml-auto">{t.hero.desc}</p></Fade>
          <Fade delay={1.3} className="mt-10"><Ctas t={t} /></Fade>
        </motion.div>
      </div>
    </section>
  );
};
